import { z } from 'zod';
/**
 * Notification — an item in the topbar bell dropdown. Distinct from
 * `Insight` (which is the proactive feed item) and `AlertItem` (the
 * /alerts page row). A `Notification` is always tied to a CEO-visible
 * surface change: an alert, an agent action, a signal threshold
 * crossed, a todo flipping to blocking, a scorecard measurable
 * flipping on/watch → off.
 *
 * The `kind` discriminates which surface the click-through should land
 * on; `<NotifDropdown>` reads it to dispatch the route.
 *
 * **Producers:**
 *   - `scorecard` — `POST /measurements` auto-ping when a measurable
 *     flips on/watch → off (issue #45, `apps/functions/src/http/router.ts`
 *     via `apps/functions/src/scorecard/auto-ping.ts`).
 *   - `alert` / `agent` / `signal` / `todo` — schemas exist; the
 *     producers land with their respective surface epics.
 *
 * **Consumers:**
 *   - `<NotifDropdown>` types its `items` prop. The bell dropdown
 *     itself is rendered in the topbar; the list endpoint
 *     (`GET /api/orgs/:o/notifications`) + the `/notifications` inbox
 *     page surface are tracked separately under the alerts/agent-log
 *     epic.
 */
export const NOTIFICATIONS_COLLECTION = 'notifications';
export const NotificationKind = z.enum(['alert', 'agent', 'signal', 'todo', 'scorecard']);
export const Notification = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** Recipient. Notifications are per-user; an org-wide event fans out. */
    userId: z.string().min(1),
    kind: NotificationKind,
    title: z.string().min(1).max(200),
    /** Optional one-line preview body. */
    body: z.string().max(400).optional(),
    /** ISO timestamp of the originating event. */
    at: z.string(),
    /** Whether the user has dismissed/marked-read this notification. */
    read: z.boolean().default(false),
    /** Optional in-app href the click should navigate to. */
    href: z.string().optional(),
});
